import airportJpeg from 'assets/images/login-portals/airport.jpg';
import airportWebp from 'assets/images/login-portals/airport.webp';
import housingJpeg from 'assets/images/login-portals/housing.jpg';
import housingWebp from 'assets/images/login-portals/housing.webp';
import modEstatesJpeg from 'assets/images/login-portals/mod-estates.jpg';
import modEstatesWebp from 'assets/images/login-portals/mod-estates.webp';
import recreationParkJpeg from 'assets/images/login-portals/recreation-park.jpg';
import recreationParkWebp from 'assets/images/login-portals/recreation-park.webp';
import defaultLoginHeroWebp from 'assets/images/xa-login-road.webp';

export type LoginPortalSlug = 'parks' | 'housing' | 'mod' | 'airports';

export interface LoginPortalHeroImage {
  readonly webp: string;
  readonly fallback: string;
}

export interface LoginPortalConfig {
  readonly slug: LoginPortalSlug;
  readonly heroImage: LoginPortalHeroImage;
}

export const DEFAULT_LOGIN_HERO_IMAGE: LoginPortalHeroImage = {
  webp: defaultLoginHeroWebp,
  fallback: defaultLoginHeroWebp,
};

export const LOGIN_PORTAL_CONFIGS: Record<LoginPortalSlug, LoginPortalConfig> = {
  parks: {
    slug: 'parks',
    heroImage: {
      webp: recreationParkWebp,
      fallback: recreationParkJpeg,
    },
  },
  housing: {
    slug: 'housing',
    heroImage: {
      webp: housingWebp,
      fallback: housingJpeg,
    },
  },
  mod: {
    slug: 'mod',
    heroImage: {
      webp: modEstatesWebp,
      fallback: modEstatesJpeg,
    },
  },
  airports: {
    slug: 'airports',
    heroImage: {
      webp: airportWebp,
      fallback: airportJpeg,
    },
  },
};

export const LOGIN_PORTAL_SLUGS = Object.keys(
  LOGIN_PORTAL_CONFIGS,
) as LoginPortalSlug[];

export function buildLoginHeroImageSet(heroImage: LoginPortalHeroImage): string {
  if (heroImage.webp === heroImage.fallback) {
    return `url("${heroImage.webp}")`;
  }

  return `image-set(url("${heroImage.webp}") type("image/webp"), url("${heroImage.fallback}") type("image/jpeg"))`;
}

export function getLoginPortalSlugFromPathname(
  pathname: string,
): LoginPortalSlug | null {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length !== 2 || segments[0] !== 'login') {
    return null;
  }

  const slug = segments[1];
  return LOGIN_PORTAL_SLUGS.includes(slug as LoginPortalSlug)
    ? (slug as LoginPortalSlug)
    : null;
}

export function getLoginPortalConfig(
  pathname: string,
): LoginPortalConfig | null {
  const slug = getLoginPortalSlugFromPathname(pathname);
  return slug ? LOGIN_PORTAL_CONFIGS[slug] : null;
}

export function getLoginHeroImageForPathname(
  pathname: string,
): LoginPortalHeroImage {
  return getLoginPortalConfig(pathname)?.heroImage ?? DEFAULT_LOGIN_HERO_IMAGE;
}

export const INSPECTIONS_TOOLKIT_SEGMENT = 'inspections-toolkit';
export const ADMINISTRATION_TOOLKIT_SEGMENT = 'administration-toolkit';

export type ToolkitSegment =
  | typeof INSPECTIONS_TOOLKIT_SEGMENT
  | typeof ADMINISTRATION_TOOLKIT_SEGMENT;

const TOOLKIT_SEGMENTS: readonly ToolkitSegment[] = [
  INSPECTIONS_TOOLKIT_SEGMENT,
  ADMINISTRATION_TOOLKIT_SEGMENT,
];

function isToolkitSegment(value: string): value is ToolkitSegment {
  return TOOLKIT_SEGMENTS.includes(value as ToolkitSegment);
}

function isLoginPortalSlug(value: string): value is LoginPortalSlug {
  return LOGIN_PORTAL_SLUGS.includes(value as LoginPortalSlug);
}

export function getSectorSlugFromDashboardPathname(
  pathname: string,
): LoginPortalSlug | null {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length !== 1) {
    return null;
  }

  const slug = segments[0];
  return isLoginPortalSlug(slug) ? slug : null;
}

export function getSectorSlugFromPathname(
  pathname: string,
): LoginPortalSlug | null {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0 || !isLoginPortalSlug(segments[0])) {
    return null;
  }

  if (segments.length === 1) {
    return segments[0];
  }

  if (segments.length === 2 && isToolkitSegment(segments[1])) {
    return segments[0];
  }

  return null;
}

export function isInspectionsToolkitPath(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 1) {
    return segments[0] === INSPECTIONS_TOOLKIT_SEGMENT;
  }

  return (
    segments.length === 2 &&
    isLoginPortalSlug(segments[0]) &&
    segments[1] === INSPECTIONS_TOOLKIT_SEGMENT
  );
}

export function isAdministrationToolkitPath(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 1) {
    return segments[0] === ADMINISTRATION_TOOLKIT_SEGMENT;
  }

  return (
    segments.length === 2 &&
    isLoginPortalSlug(segments[0]) &&
    segments[1] === ADMINISTRATION_TOOLKIT_SEGMENT
  );
}

export function isSectorDashboardPath(pathname: string): boolean {
  return getSectorSlugFromDashboardPathname(pathname) !== null;
}

export function isDashboardPath(pathname: string): boolean {
  return (
    pathname === '/' ||
    isSectorDashboardPath(pathname) ||
    isInspectionsToolkitPath(pathname)
  );
}

export function buildInspectionsToolkitPath(
  sectorSlug?: LoginPortalSlug | null,
): string {
  return sectorSlug
    ? `/${sectorSlug}/${INSPECTIONS_TOOLKIT_SEGMENT}`
    : `/${INSPECTIONS_TOOLKIT_SEGMENT}`;
}

export function buildAdministrationToolkitPath(
  sectorSlug?: LoginPortalSlug | null,
): string {
  return sectorSlug
    ? `/${sectorSlug}/${ADMINISTRATION_TOOLKIT_SEGMENT}`
    : `/${ADMINISTRATION_TOOLKIT_SEGMENT}`;
}

function isValidAuthenticatedReturnPath(returnPath: string): boolean {
  return (
    returnPath !== '/' &&
    !returnPath.startsWith('/login') &&
    (isSectorDashboardPath(returnPath) ||
      isInspectionsToolkitPath(returnPath) ||
      isAdministrationToolkitPath(returnPath))
  );
}

export function getCanonicalAppPath(pathname: string): string | null {
  const storedSector = getStoredActiveSectorSlug();
  if (!storedSector) {
    return null;
  }

  if (pathname === `/${INSPECTIONS_TOOLKIT_SEGMENT}`) {
    return buildInspectionsToolkitPath(storedSector);
  }

  if (pathname === `/${ADMINISTRATION_TOOLKIT_SEGMENT}`) {
    return buildAdministrationToolkitPath(storedSector);
  }

  return null;
}

export function buildSectorDashboardPath(slug: LoginPortalSlug): string {
  return `/${slug}`;
}

export function buildLoginPathForSector(slug: LoginPortalSlug): string {
  return `/login/${slug}`;
}

const ACTIVE_SECTOR_STORAGE_KEY = 'activeLoginPortal';

export function syncActiveSectorFromPathname(pathname: string): void {
  const slug = getSectorSlugFromPathname(pathname);
  if (slug) {
    sessionStorage.setItem(ACTIVE_SECTOR_STORAGE_KEY, slug);
  }
}

export function getStoredActiveSectorSlug(): LoginPortalSlug | null {
  const stored = sessionStorage.getItem(ACTIVE_SECTOR_STORAGE_KEY);
  return stored && isLoginPortalSlug(stored) ? stored : null;
}

export function getActiveSectorSlug(pathname: string): LoginPortalSlug | null {
  return getSectorSlugFromPathname(pathname) ?? getStoredActiveSectorSlug();
}

export function getDashboardHomePath(pathname: string): string {
  const slug = getActiveSectorSlug(pathname);
  return slug ? buildSectorDashboardPath(slug) : '/';
}

export function clearActiveSectorSlug(): void {
  sessionStorage.removeItem(ACTIVE_SECTOR_STORAGE_KEY);
}

export function getPostAuthRedirectPath(
  loginPathname: string,
  returnPath?: string,
): string {
  const loginSlug = getLoginPortalSlugFromPathname(loginPathname);
  if (loginSlug) {
    return buildSectorDashboardPath(loginSlug);
  }

  if (returnPath && isValidAuthenticatedReturnPath(returnPath)) {
    return returnPath;
  }

  return '/';
}

export function getUnauthenticatedLoginTarget(pathname: string): {
  readonly to: string;
  readonly returnPath?: string;
} {
  const sectorSlug = getSectorSlugFromPathname(pathname);
  if (sectorSlug) {
    return {
      to: buildLoginPathForSector(sectorSlug),
      returnPath: pathname,
    };
  }

  return {
    to: '/login',
    ...(pathname !== '/' ? { returnPath: pathname } : {}),
  };
}

export function getLogoutLoginPath(pathname: string): string {
  const sectorSlug = getActiveSectorSlug(pathname);
  return sectorSlug ? buildLoginPathForSector(sectorSlug) : '/login';
}
